"use client";

import Link from "next/link";
import SizeableText from "@/components/SizeableText";
import { AuthCard } from "../components";
import styles from "../shared.module.css";

interface FormProps {
  mainPhone: string;
}

const PLAID_LOGO_URL =
  "https://cdn.plaid.com/link/2.0.2397/assets/images/plaid-logo-black-bg.png";

function Footer() {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerMain}>
        <div className={styles.footerLeft}>
          <span className={styles.footerLocation}>Brooklyn, New York</span>
        </div>
        <div className={styles.footerRight}>
          <div className={styles.footerStatus}>
            <span className={styles.footerStatusDot} />
            <span className={styles.footerStatusText}>
              All systems operational
            </span>
          </div>
        </div>
      </div>
      <div className={styles.footerBottom}>
        <div className={styles.footerBottomLinks}>
          <Link href="/terms" className={styles.footerBottomLink}>
            Terms and Conditions
          </Link>
          <Link href="/privacy" className={styles.footerBottomLink}>
            Privacy Policy
          </Link>
        </div>
        <span className={styles.footerCopyright}>
          2025 Passage, All Rights Reserved
        </span>
      </div>
    </footer>
  );
}

export function Form({ mainPhone }: FormProps) {
  const handleSms = () => {
    const message = "I want to talk to my money";
    // Use ?&body= for cross-platform compatibility (iOS and Android)
    window.location.href = `sms:${mainPhone}?&body=${encodeURIComponent(message)}`;
  };

  return (
    <div className={styles.landingPage}>
      {/* First viewport - 100vh container with banner pinned to bottom */}
      <div className={styles.landingWrapper}>
        {/* Hero section - centers the main content */}
        <div className={styles.landingHero}>
          {/* Badges */}
          <div className={styles.badges}>
            <div className={`${styles.badge} ${styles.badgeGreen}`}>
              <span>🚀</span>
              <span className={`${styles.badgeText} ${styles.badgeTextGreen}`}>
                Raised $2.5M from Mischief and Saga
              </span>
            </div>
            <div className={`${styles.badge} ${styles.badgeBlue}`}>
              <span className={styles.badgeIcon}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={PLAID_LOGO_URL} alt="Plaid" />
              </span>
              <span className={`${styles.badgeText} ${styles.badgeTextBlue}`}>
                Built by the team behind Plaid
              </span>
            </div>
          </div>

          <AuthCard>
            {/* Title */}
            <h1 className={styles.authTitle}>
              Talk to your <em className={styles.authTitleItalic}>money</em>
            </h1>
            <SizeableText
              color="secondary"
              size="medium"
              style={{ marginBottom: "20px" }}
            >
              SMS to save and grow your cash
            </SizeableText>

            {/* Primary CTA Button */}
            <button
              type="button"
              className={styles.heroCta}
              onClick={handleSms}
            >
              <span className={styles.heroCtaText}>Start chatting</span>
            </button>
          </AuthCard>

          {/* Social Links */}
          <div className={styles.socialLinks}>
            <a
              href="https://x.com/passagemoney"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              title="X (Twitter)"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/passage-auth/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              title="LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
      {/* Footer below the fold */}
      <Footer />
    </div>
  );
}
